
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SubscriptionCard from "./SubscriptionCard";
import { useAuth } from "@/context/AuthContext";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";

const PricingSection: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "quarterly" | "annual">("monthly");
  const { user } = useAuth();

  if (isAndroidTWA()) {
    return <AndroidNeutralNotice />;
  }

  const plans = {
    trial15: {
      title: "Experimente 15 dias",
      price: 27.90,
      features: [
        "Acesso completo por 15 dias",
        "Todos os métodos premium liberados",
        "Estatísticas em tempo real",
        "Palpites com IA",
        "Sem compromisso mensal",
        "Ideal para conhecer a plataforma",
      ],
      isPopular: false,
      visible: false,
    },
    basic: {
      title: "Palpite Guru Mensal",
      monthly: 44.90,
      features: [
        "Acesso completo a todos os palpites",
        "10 palpites de Milhar por dia",
        "10 palpites de Centena por dia",
        "4 palpites de Grupo por dia",
        "10 palpites de Duque por dia",
        "10 palpites de Terno por dia"
      ],
      isPopular: false,
      visible: true
    },
    premium: {
      title: "Palpite Guru Trimestral",
      quarterly: 59.90,
      features: [
        "Todos os recursos do Plano Mensal",
        "Economia de R$ 20,00 por trimestre",
        "Acesso completo a todos os palpites",
        "10 palpites de Milhar por dia",
        "10 palpites de Centena por dia",
        "4 palpites de Grupo por dia",
        "10 palpites de Duque e Terno por dia"
      ],
      isPopular: true,
      visible: true
    },
    vip: {
      title: "Palpite Guru Anual",
      annual: 189.90,
      features: [
        "Todos os recursos dos outros planos",
        "Maior economia: R$ 124,00 por ano",
        "Acesso completo e ilimitado",
        "10 palpites de Milhar por dia",
        "10 palpites de Centena por dia",
        "4 palpites de Grupo por dia",
        "10 palpites de Duque e Terno por dia",
        "Suporte prioritário"
      ],
      isPopular: false,
      visible: true
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8" id="escolha-seu-plano">
        <h2 className="text-3xl font-bold mb-4">Escolha seu Plano</h2>
        <p className="text-muted-foreground mb-6">
          Tenha acesso completo a todos os palpites do jogo do bicho
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {plans.trial15.visible && (
            <SubscriptionCard
              plan="trial15"
              title={plans.trial15.title}
              price={plans.trial15.price}
              billingPeriod="biweekly"
              features={plans.trial15.features}
              isPopular={plans.trial15.isPopular}
              isCurrentPlan={false}
            />
          )}

          <SubscriptionCard
            plan="basic"
            title={plans.basic.title}
            price={plans.basic.monthly}
            billingPeriod="monthly"
            features={plans.basic.features}
            isPopular={plans.basic.isPopular}
            isCurrentPlan={false}
          />
          
          <SubscriptionCard
            plan="premium"
            title={plans.premium.title}
            price={plans.premium.quarterly}
            billingPeriod="quarterly"
            features={plans.premium.features}
            isPopular={plans.premium.isPopular}
            isCurrentPlan={false}
          />
          
          <SubscriptionCard
            plan="vip"
            title={plans.vip.title}
            price={plans.vip.annual}
            billingPeriod="annual"
            features={plans.vip.features}
            isPopular={plans.vip.isPopular}
            isCurrentPlan={false}
          />
        </div>
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-center">Plano Gratuito</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-muted-foreground mb-4">
            Experimente nossos recursos básicos gratuitamente
          </p>
          <ul className="text-sm space-y-1 mb-4">
            <li>• 2 palpites de Milhar por dia</li>
            <li>• 2 palpites de Centena por dia</li>
            <li>• Sem acesso aos palpites de Grupo, Duque e Terno</li>
          </ul>
          <Button variant="outline" className="w-full max-w-sm">
            Continuar Gratuito
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricingSection;
